import { Button as ShadcnButton, buttonVariants } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { isReactComponent } from "@/utils/is-react-component";
import type { VariantProps } from "class-variance-authority";
import * as React from "react";
import type { FC, ReactNode } from "react";

export type ButtonProps = Omit<React.ComponentProps<"button">, "color"> &
    VariantProps<typeof buttonVariants> & {
        isLoading?: boolean;
        /** Icon element or component rendered before the label (destination convenience). */
        iconLeading?: FC<{ className?: string }> | ReactNode;
        iconTrailing?: FC<{ className?: string }> | ReactNode;
        /** Optional link navigation — renders as `<a>` when set (destination-shaped, not RAC Link). */
        href?: string;
    };

function renderIcon(icon: FC<{ className?: string }> | ReactNode, className?: string) {
    if (!icon) {
        return null;
    }
    if (React.isValidElement(icon)) {
        return icon;
    }
    if (isReactComponent(icon)) {
        const Icon = icon as FC<{ className?: string }>;
        return <Icon className={className ?? "size-4"} />;
    }
    return icon;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
    (
        {
            className,
            variant,
            size = "default",
            isLoading,
            children,
            disabled,
            iconLeading,
            iconTrailing,
            href,
            ...props
        },
        ref,
    ) => {
        const label =
            typeof children === "string" || typeof children === "number" ? (
                <span>{children}</span>
            ) : (
                children
            );

        const content = (
            <>
                {isLoading ? (
                    <svg
                        fill="none"
                        aria-hidden="true"
                        viewBox="0 0 20 20"
                        className="mr-2 h-4 w-4 animate-spin text-current"
                    >
                        <circle className="opacity-25" cx="10" cy="10" r="8" stroke="currentColor" strokeWidth="2" />
                        <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 10a6 6 0 016-6v2a4 4 0 00-4 4H4z"
                        />
                    </svg>
                ) : null}
                {iconLeading ? (
                    <span className="mr-1.5 inline-flex shrink-0 items-center">{renderIcon(iconLeading)}</span>
                ) : null}
                {/* Element-wrapped labels avoid Chrome Translate detach + removeChild crashes. */}
                {label}
                {iconTrailing ? (
                    <span className="ml-1.5 inline-flex shrink-0 items-center">{renderIcon(iconTrailing)}</span>
                ) : null}
            </>
        );

        if (href) {
            return (
                <a
                    href={disabled || isLoading ? undefined : href}
                    aria-disabled={disabled || isLoading || undefined}
                    className={cn(
                        buttonVariants({ variant, size }),
                        className,
                        (disabled || isLoading) && "pointer-events-none opacity-50",
                    )}
                    {...(props as unknown as React.AnchorHTMLAttributes<HTMLAnchorElement>)}
                >
                    {content}
                </a>
            );
        }

        return (
            <ShadcnButton
                ref={ref as React.Ref<HTMLButtonElement>}
                disabled={disabled || isLoading}
                variant={variant}
                size={size}
                className={cn(className)}
                {...props}
            >
                {content}
            </ShadcnButton>
        );
    },
);

Button.displayName = "Button";
